import React, { createContext, useReducer, useContext, ReactNode } from "react";

interface TableSettings {
  allTables: boolean;
  playlistsTable: boolean;
  tracksTable: boolean;
  guestCuratorsTable: boolean;
  libraryTable: boolean;
}

interface SettingsContextState {
  tableSettings: TableSettings;
  toggleAllCompressUITables: () => void;
  togglePlaylistsCompressUITable: () => void;
  toggleCompressUITracksTable: () => void;
  toggleGuestCuratorsTable: () => void;
  toggleLibraryTable: () => void;
  isSideNavMenuOpen: boolean;
  toggleSideNavMenu: () => void;
}

type SettingsAction =
  | { type: "TOGGLE_COMPRESS_ALL_TABLES" }
  | { type: "TOGGLE_COMPRESS_PLAYLISTS_TABLE" }
  | { type: "TOGGLE_COMPRESS_TRACKS_TABLE" }
  | { type: "TOGGLE_COMPRESS_GUEST_CURATORS_TABLE" }
  | { type: "TOGGLE_COMPRESS_LIBRARY_TABLE" }
  | { type: "TOGGLE_SIDE_NAV_MENU" };

const initialSettingsState: SettingsContextState = {
  tableSettings: JSON.parse(
    sessionStorage.getItem("tableSettings") ||
      JSON.stringify({
        allTables: false,
        playlistsTable: true,
        tracksTable: true,
        guestCuratorsTable: true,
        libraryTable: true,
      })
  ),
  toggleAllCompressUITables: function () {},
  togglePlaylistsCompressUITable: function () {},
  toggleCompressUITracksTable: function () {},
  toggleGuestCuratorsTable: function () {},
  toggleLibraryTable: function () {},
  isSideNavMenuOpen: JSON.parse(
    sessionStorage.getItem("isSideNavMenuOpen") || "false"
  ),
  toggleSideNavMenu: function () {},
};

const settingsReducer = (
  state: SettingsContextState,
  action: SettingsAction
): SettingsContextState => {
  let newState;
  switch (action.type) {
    case "TOGGLE_COMPRESS_ALL_TABLES":
      newState = {
        ...state,
        tableSettings: {
          ...state.tableSettings,
          allTables: !state.tableSettings.allTables,
        },
      };
      break;
    case "TOGGLE_COMPRESS_PLAYLISTS_TABLE":
      newState = {
        ...state,
        tableSettings: {
          ...state.tableSettings,
          playlistsTable: !state.tableSettings.playlistsTable,
        },
      };
      break;
    case "TOGGLE_COMPRESS_TRACKS_TABLE":
      newState = {
        ...state,
        tableSettings: {
          ...state.tableSettings,
          tracksTable: !state.tableSettings.tracksTable,
        },
      };
      break;
    case "TOGGLE_COMPRESS_GUEST_CURATORS_TABLE":
      newState = {
        ...state,
        tableSettings: {
          ...state.tableSettings,
          guestCuratorsTable: !state.tableSettings.guestCuratorsTable,
        },
      };
      break;
    case "TOGGLE_COMPRESS_LIBRARY_TABLE":
      newState = {
        ...state,
        tableSettings: {
          ...state.tableSettings,
          libraryTable: !state.tableSettings.libraryTable,
        },
      };
      break;
    case "TOGGLE_SIDE_NAV_MENU":
      newState = {
        ...state,
        isSideNavMenuOpen: !state.isSideNavMenuOpen,
      };
      break;
    default:
      return state;
  }

  sessionStorage.setItem(
    "tableSettings",
    JSON.stringify(newState.tableSettings)
  );
  sessionStorage.setItem(
    "isSideNavMenuOpen",
    JSON.stringify(newState.isSideNavMenuOpen)
  );
  return newState;
};

const SettingsContext = createContext<SettingsContextState | undefined>(
  undefined
);

export const SettingsProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(settingsReducer, initialSettingsState);

  const toggleAllCompressUITables = (): void => {
    dispatch({ type: "TOGGLE_COMPRESS_ALL_TABLES" });
  };

  const togglePlaylistsCompressUITable = (): void => {
    dispatch({ type: "TOGGLE_COMPRESS_PLAYLISTS_TABLE" });
  };

  const toggleCompressUITracksTable = (): void => {
    dispatch({ type: "TOGGLE_COMPRESS_TRACKS_TABLE" });
  };
  const toggleGuestCuratorsTable = (): void => {
    dispatch({ type: "TOGGLE_COMPRESS_GUEST_CURATORS_TABLE" });
  };
  const toggleLibraryTable = (): void => {
    dispatch({ type: "TOGGLE_COMPRESS_LIBRARY_TABLE" });
  };
  const toggleSideNavMenu = (): void => {
    dispatch({ type: "TOGGLE_SIDE_NAV_MENU" });
  };
  return (
    <SettingsContext.Provider
      value={{
        tableSettings: state.tableSettings,
        toggleAllCompressUITables,
        togglePlaylistsCompressUITable,
        toggleCompressUITracksTable,
        toggleGuestCuratorsTable,
        toggleLibraryTable,
        isSideNavMenuOpen: state.isSideNavMenuOpen,
        toggleSideNavMenu,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = (): SettingsContextState => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};
